import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, INR, calcEMI } from "@/lib/api";
import { ArrowRight, CheckCircle2, Wallet, Briefcase, Home, Car, Gem, GraduationCap, Building2, Landmark, Cog } from "lucide-react";

const ICONS = { wallet: Wallet, briefcase: Briefcase, home: Home, car: Car, gem: Gem, "graduation-cap": GraduationCap, "building-2": Building2, landmark: Landmark, cog: Cog };

export default function Loans() {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/loan-products").then(({data}) => setProducts(data)); }, []);
  const single = slug ? products.find(p => p.slug === slug) : null;

  if (slug && !single && products.length) {
    return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-white/60">Product not found. <Link to="/loans" className="gold">Back to all loans</Link></div>;
  }

  if (single) {
    const Icon = ICONS[single.icon] || Wallet;
    const emi = calcEMI(single.min_amount * 4, single.interest_rate_from, 36);
    return (
      <div data-testid="loan-detail" className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <Link to="/loans" className="text-white/50 text-sm hover:text-[#D4AF37]">← All loan products</Link>
        <div className="mt-6 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center">
              <Icon size={26} className="text-[#D4AF37]"/>
            </div>
            <h1 className="mt-6 font-display text-5xl text-white leading-tight">{single.name}</h1>
            <p className="mt-4 text-white/70 text-lg max-w-xl">{single.tagline}</p>
            <p className="mt-4 text-white/60 max-w-xl leading-relaxed">{single.description}</p>
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {single.features.map((f, i) => (
                <div key={i} className="flex items-start gap-3 text-white/80">
                  <CheckCircle2 size={18} className="text-[#D4AF37] mt-0.5 flex-shrink-0"/>
                  <span>{f}</span>
                </div>
              ))}
            </div>
            <Link to={`/apply?loan_type=${encodeURIComponent(single.name)}`} className="sk-btn-gold mt-10 inline-flex items-center gap-2">
              Apply for {single.name} <ArrowRight size={16}/>
            </Link>
          </div>

          <aside className="lg:col-span-5">
            <div className="sk-card-dark p-7">
              <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">At a glance</div>
              <div className="sk-divider my-4"/>
              <div className="grid grid-cols-2 gap-y-5 text-sm">
                <div className="text-white/45">Interest rate</div><div className="text-white text-right">{single.interest_rate_from}% – {single.interest_rate_to}%</div>
                <div className="text-white/45">Loan amount</div><div className="text-white text-right">{INR(single.min_amount)} – {INR(single.max_amount)}</div>
                <div className="text-white/45">Tenure</div><div className="text-white text-right">{single.min_tenure_months} – {single.max_tenure_months} months</div>
              </div>
              <div className="mt-6 p-4 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/25">
                <div className="text-xs text-white/60">Illustrative EMI (₹{Math.round(single.min_amount*4/100000)}L · 3 yr · {single.interest_rate_from}% p.a.)</div>
                <div className="font-display text-3xl text-[#D4AF37] mt-2">{INR(emi)}</div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="loans-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="max-w-2xl">
        <div className="sk-badge sk-badge-gold">All loan products</div>
        <h1 className="mt-4 font-display text-5xl text-white leading-tight">The full <span className="gold italic">SK Group</span> loan portfolio</h1>
        <p className="mt-4 text-white/60">Choose the loan that fits your goal. Interest rates from 7.5% p.a. and tenures up to 30 years.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
        {products.map((p) => {
          const Icon = ICONS[p.icon] || Wallet;
          return (
            <Link key={p.id} to={`/loans/${p.slug}`} data-testid={`loans-card-${p.slug}`} className="sk-card-dark p-7 block">
              <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center mb-5"><Icon size={22} className="text-[#D4AF37]"/></div>
              <div className="font-display text-2xl text-white">{p.name}</div>
              <p className="text-white/60 mt-2 text-sm">{p.tagline}</p>
              <div className="sk-divider my-5"/>
              <div className="text-xs text-white/50">From <span className="text-[#D4AF37] font-medium">{p.interest_rate_from}% p.a.</span> · Up to <span className="text-white">{INR(p.max_amount)}</span></div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
